import { classes } from 'html-classes';
import type React from 'react';

import Icon from '~/components/Icon/Icon';
import LimitReachedTooltip from '~/components/isolated/LimitReachedTooltip';

import {
  COLOR_VARIANT,
  ColorVariantMap,
  SIZE,
  SizeMap,
  VARIANT,
  VariantMap,
} from './constants';
import type { SimpleCounterProps } from './interfaces';
import styles from './SimpleCounter.module.scss';

const PRICE_LENGTH_THRESHOLDS = {
  SHORT: 8,
  MEDIUM: 11,
  LONG: 13,
} as const;

const SimpleCounter: React.FC<SimpleCounterProps> = ({
  count,
  onIncrement,
  onDecrement,
  size = SIZE.L,
  variant = VARIANT.primary,
  colorVariant = COLOR_VARIANT.default,
  className,
  limit,
  totalPrice,
  hideTooltip = false,
  limitReachedText,
}) => {
  const incrementDisabled = limit !== undefined && count >= limit;

  const handleDecrement = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDecrement();
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onIncrement();
  };

  const getPriceLengthClass = () => {
    if (!totalPrice) {
      return '';
    }
    const length = totalPrice.length;
    if (length <= PRICE_LENGTH_THRESHOLDS.SHORT) {
      return '';
    }
    if (length <= PRICE_LENGTH_THRESHOLDS.MEDIUM) {
      return styles.priceMedium;
    }
    if (length <= PRICE_LENGTH_THRESHOLDS.LONG) {
      return styles.priceLong;
    }
    return styles.priceVeryLong;
  };

  return (
    <div className={styles.wrapper}>
      {!hideTooltip && (
        <LimitReachedTooltip isLimitReached={incrementDisabled}>
          {limitReachedText}
        </LimitReachedTooltip>
      )}
      <div
        className={classes([
          styles.counter,
          SizeMap.get(size),
          VariantMap.get(variant),
          ColorVariantMap.get(colorVariant),
          className,
        ])}
      >
        <button
          type="button"
          className={styles.button}
          onClick={handleDecrement}
          aria-label="Decrease count"
        >
          <Icon type="minus-3.1" size={18} />
        </button>
        <span
          className={classes([styles.count, totalPrice && styles.withPrice])}
        >
          <span className={styles.countNumber}>{count}</span>
          {totalPrice && (
            <span
              className={classes([styles.totalPrice, getPriceLengthClass()])}
            >
              {totalPrice}
            </span>
          )}
        </span>
        <button
          type="button"
          className={styles.button}
          onClick={handleIncrement}
          aria-label="Increase count"
          disabled={incrementDisabled}
        >
          <Icon type="plus-3.1" size={16} />
        </button>
      </div>
    </div>
  );
};

export default SimpleCounter;
